import type { User, Workspace as HeldWorkspace } from "@/types/serializers"

import type {
  Page,
  RequestBody,
  SerialisableBody,
  TransientData,
  VisitMethod,
} from "./types"

// The host contract: the one interface a page reads its runtime through. It
// names no framework, React included; the binding (host-react.tsx) puts
// hooks over it, and a runtime (inertia-host.ts, driver.tsx) provides it.
//
// Four concepts, from the layer model:
//   Session  app-wide, one per app: who is signed in.
//   Entry    one page at one URL in a layer; identity is name + URL.
//   Visit    a request and its outcome: props, flash, transient, errors.
//   Layer    a navigator with its own entries: the root (the tab, over
//            Inertia for now) or a child (an embedded Driver over an entry).

export type Readable<T> = {
  get(): T
  subscribe(onChange: () => void): () => void
}

export type Writable<T> = Readable<T> & {
  set(value: T): void
}

// A value a runtime owns and its readers subscribe to. `set` notifies only
// when the value changes identity.
export function writable<T>(initial: T): Writable<T> {
  let value = initial
  const listeners = new Set<() => void>()

  return {
    get: () => value,
    set(next) {
      if (Object.is(next, value)) return
      value = next
      for (const listener of [...listeners]) listener()
    },
    subscribe(onChange) {
      listeners.add(onChange)
      return () => listeners.delete(onChange)
    },
  }
}

// A value computed from other readables, recomputed only when one of them
// changes identity, so a reader comparing snapshots sees a stable value.
export function derived<T>(
  sources: Readable<unknown>[],
  compute: () => T,
): Readable<T> {
  let inputs: unknown[] | null = null
  let value: T

  return {
    get() {
      const next = sources.map((source) => source.get())
      if (
        inputs === null ||
        next.some((input, index) => !Object.is(input, inputs![index]))
      ) {
        inputs = next
        value = compute()
      }
      return value
    },
    subscribe(onChange) {
      const stops = sources.map((source) => source.subscribe(onChange))
      return () => stops.forEach((stop) => stop())
    },
  }
}

export type Session =
  | {
      authenticated: true
      user: User
      workspaces: HeldWorkspace[]
      impersonating: boolean
      featureToggles: string[]
    }
  | {
      authenticated: false
      user: null
      workspaces: HeldWorkspace[]
      impersonating: boolean
      featureToggles: string[]
    }

export const ANONYMOUS_SESSION: Session = {
  authenticated: false,
  user: null,
  workspaces: [],
  impersonating: false,
  featureToggles: [],
}

export type Entry = {
  // The page name, as the pages map keys it (`search/classes/show`).
  name: string
  url: string
  // The URL's path parameters, keyed in camelCase (teacherId, classId) as
  // the route helpers in @/routes take them.
  pathParams: Record<string, string>
}

export type FlashMessage = { namespace: string; key: string } | string

export type Flash = {
  success?: FlashMessage
  alert?: FlashMessage
  // A sheet the page the visit landed on opens at once: the trigger it
  // names. Only the iframe sheet reads it; the iframe task retires it.
  dialog_id?: string
}

export type Visit = {
  pending: boolean
  props: SerialisableBody
  flash: Flash
  transient: TransientData
  errors: Record<string, string>
}

export type VisitRequest = {
  href: string
  method?: `${VisitMethod}`
  data?: RequestBody
  // The outcome updates the current entry in place and replaces its history
  // slot rather than adding one.
  replace?: boolean
  // The scroll position is kept when the outcome commits.
  preserveScroll?: boolean
  // The entry keeps its URL whatever URL the outcome reports: for a page
  // that answers a mutation in place rather than redirecting.
  preserveUrl?: boolean
  // The body is sent as multipart form data; a body holding a file always
  // is, so this only matters for a form whose files are optional.
  formData?: boolean
}

// What an entry's forms report about unsaved work. The guard holds dirty
// sources by identity; a dirty entry is one with any source dirty.
export type LeaveGuard = {
  report(source: symbol, dirty: boolean): void
  dirty: Readable<boolean>
}

export function createLeaveGuard(): LeaveGuard {
  const sources = new Set<symbol>()
  const dirty = writable(false)

  return {
    report(source, isDirty) {
      if (isDirty) sources.add(source)
      else sources.delete(source)
      dirty.set(sources.size > 0)
    },
    dirty,
  }
}

export interface Host {
  session: Readable<Session>
  entry: Readable<Entry>
  visit: Readable<Visit>
  // Settles with the visit the request committed, so a page can act on the
  // outcome (close once its save has no errors). A visit that is cancelled
  // or fails outside the page protocol never settles.
  navigate(request: VisitRequest): Promise<Visit>
  guard: LeaveGuard
  // Commits a page a child layer resolved but declined: the child's fetch
  // already followed any redirect and consumed its flash, so the page is
  // committed as delivered, never requested again.
  receive(page: Page): void
}
